import { useSyncExternalStore } from 'react';

// Thin client for GET /api/flags (backend/routers/flags.py — public, no auth).
// Mirrors stores/tierConfigStore.ts: fetch-once module cache + useSyncExternalStore,
// so the app has exactly one pattern for "load a public config blob and subscribe to it".
//
// Effective values only — this does not read `meta`/classification (that's the
// admin FlagsPanel's job). Any flag not yet loaded (or not present in the
// registry) reads as `false`, which is the correct fail-closed default for a
// dormancy/kill switch per the flag registry's own convention
// (services/feature_flags.py: flag() fails closed on read errors).

type Flags = Record<string, boolean>;

let state: Flags = {};
let loaded = false;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => listeners.delete(listener); };
const getSnapshot = () => state;
const getServerSnapshot = () => state;

/** Read a single flag's effective value. Defaults to `false` until loaded. */
export const useFeatureFlag = (name: string): boolean => {
  const flags = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return Boolean(flags[name]);
};

export async function loadFeatureFlags(): Promise<void> {
  if (loaded) return;
  try {
    const response = await fetch('/api/flags');
    if (!response.ok) return;
    const data = await response.json();
    state = data?.flags && typeof data.flags === 'object' ? data.flags : {};
    loaded = true;
    listeners.forEach((listener) => listener());
  } catch {
    // Keep the fail-closed default ({} => every flag reads false) when the
    // backend is unavailable.
  }
}
